import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, map, of, switchMap, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { MODULOS, type Modulo } from '../modulos';
import type {
  AjustarLogAcceso,
  AjustarLogAccesoRequest,
  JsModulo,
  LogAcceso,
  Organizacion,
} from './interfaces/auth.interface';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private base = `${environment.apiUrl}/auth`;

  readonly sesion = signal<LogAcceso | null>(null);
  /** Módulo de Administración (Consultorías u Organizaciones) en el que se está trabajando. */
  readonly modulo = signal<Modulo | null>(null);
  readonly moduloActual = computed(() =>
    this.sesion()?.modulosAbiertos.find((m) => m.cveModulo === this.modulo()?.cve),
  );
  readonly tpUsuario = computed(
    () => this.moduloActual()?.tpUsuario ?? this.sesion()?.usuario.tpUsuario ?? '',
  );
  readonly saliendo = signal(false);

  getLogAcceso(): Observable<LogAcceso> {
    return this.http.post<LogAcceso>(`${this.base}/getLogAcceso`, {});
  }

  getLstOrganizacionPersona(): Observable<Organizacion[]> {
    return this.http.post<Organizacion[]>(`${this.base}/getLstOrganizacionPersona`, {});
  }

  /**
   * Deja la sesión con el módulo abierto. Si se entró directo por URL estando en
   * otro módulo, entra con la organización de ese módulo; la API valida que la
   * organización tenga contratado este.
   */
  entrarAlModulo(modulo: Modulo): Observable<LogAcceso> {
    return this.getLogAcceso().pipe(
      switchMap((sesion) => {
        if (sesion.modulosAbiertos.some((m) => m.cveModulo === modulo.cve)) {
          return of(sesion);
        }

        const organizacionId = sesion.modulosAbiertos[0]?.organizacionId;
        if (!organizacionId) return throwError(() => new Error('Sin organización'));

        return this.http.post<JsModulo[]>(`${this.base}/getJsModulos`, { organizacionId }).pipe(
          map((modulos) => modulos.find((m) => m.cve === modulo.cve)),
          switchMap((encontrado) =>
            encontrado
              ? this.ajustarLogAcceso({
                  status: 'Login',
                  moduloId: encontrado.moduloId,
                  organizacionId,
                })
              : throwError(() => new Error('La organización no tiene este módulo')),
          ),
          switchMap(() => this.getLogAcceso()),
        );
      }),
      tap((sesion) => {
        this.sesion.set(sesion);
        this.modulo.set(modulo);
      }),
    );
  }

  ajustarLogAcceso(datos: AjustarLogAccesoRequest): Observable<AjustarLogAcceso> {
    return this.http.post<AjustarLogAcceso>(`${this.base}/ajustarLogAcceso`, datos);
  }

  cambiarModulo(): void {
    const modulo = this.moduloActual();
    this.salir({
      status: 'BackToHome',
      moduloId: modulo?.moduloId,
      organizacionId: modulo?.organizacionId,
    });
  }

  // Igual que cambiarModulo, pero unilogin abre la organización y la sección de este módulo.
  irAModulos(): void {
    const modulo = this.moduloActual();
    const destino = modulo
      ? `${environment.loginUrl}/modulos?organizacion=${modulo.organizacionId}&modulo=${modulo.cveModulo}`
      : environment.loginUrl;
    this.salir(
      { status: 'BackToHome', moduloId: modulo?.moduloId, organizacionId: modulo?.organizacionId },
      destino,
    );
  }

  cerrarSesion(): void {
    this.salir({ status: 'Exit_Logout' });
  }

  // replace() para que el botón "atrás" no regrese a una pantalla sin sesión.
  irAlLogin(url = environment.loginUrl): void {
    this.sesion.set(null);
    window.location.replace(url);
  }

  // Pase lo que pase se regresa al login: si la sesión ya estaba cerrada, la API responde 401.
  private salir(datos: AjustarLogAccesoRequest, destino?: string): void {
    this.saliendo.set(true);
    this.ajustarLogAcceso(datos).subscribe({
      next: () => this.irAlLogin(destino),
      error: () => this.irAlLogin(destino),
    });
  }
}

/** El módulo de Administración que se abrió más recientemente; sin ninguno, Consultorías. */
export function moduloReciente(sesion: LogAcceso): Modulo {
  const abierto = sesion.modulosAbiertos.find((m) => MODULOS.some((x) => x.cve === m.cveModulo));
  return MODULOS.find((x) => x.cve === abierto?.cveModulo) ?? MODULOS[0];
}
